// src/components/shops/postTemplates/Chalkboard.tsx
//
// The full board: sections, items, descriptions and prices, read out of a
// post's body the way Field Note and Recipe Card already read their own
// structured content. A photo would compete with the amount of text here
// rather than support it, so — like Menu Board and Marquee — this template
// does not take one.

import SocialBrewBadge from '../SocialBrewBadge'
import { parseMenu, type TemplateProps } from './types'

export default function Chalkboard({
  title, body, shopName, rating, ratingCount, scale = 1,
}: TemplateProps) {
  const px = (n: number) => `${n * scale}px`
  const menu = parseMenu(body)

  return (
    <div
      style={{ background: '#1c1008', borderRadius: px(4), padding: `${px(22)} ${px(18)} ${px(16)}` }}
      data-shop={shopName}
    >
      <h3
        className="font-display"
        style={{
          fontSize: px(24),
          lineHeight: 1.1,
          letterSpacing: '-0.01em',
          color: '#f4ead9',
          fontWeight: 700,
          margin: 0,
          textAlign: 'center',
          textWrap: 'balance',
        }}
      >
        {title}
      </h3>

      {menu ? (
        <div style={{ marginTop: px(18) }}>
          {menu.sections.map((section, si) => (
            <div key={si} style={{ marginTop: si === 0 ? 0 : px(18) }}>
              {section.heading && (
                <p
                  style={{
                    fontSize: px(10),
                    letterSpacing: '0.26em',
                    textTransform: 'uppercase',
                    color: '#c8853a',
                    fontWeight: 700,
                    borderBottom: `${px(1)} solid rgba(240,226,203,0.18)`,
                    paddingBottom: px(6),
                    marginBottom: px(10),
                  }}
                >
                  {section.heading}
                </p>
              )}
              {section.items.map((item, ii) => (
                <div
                  key={ii}
                  className="flex items-start justify-between"
                  style={{ gap: px(10), marginBottom: px(9) }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: px(13.5), fontWeight: 700, color: '#f4ead9', margin: 0 }}>
                      {item.name}
                    </p>
                    {item.description && (
                      <p
                        style={{
                          fontSize: px(11),
                          lineHeight: 1.45,
                          color: 'rgba(240,226,203,0.58)',
                          margin: `${px(2)} 0 0`,
                        }}
                      >
                        {item.description}
                      </p>
                    )}
                  </div>
                  <span
                    className="font-display"
                    style={{ fontSize: px(13.5), fontWeight: 700, color: '#e0a45f', flexShrink: 0 }}
                  >
                    ${item.price}
                  </span>
                </div>
              ))}
            </div>
          ))}

          {menu.notes.length > 0 && (
            <div style={{ marginTop: px(14), borderTop: `${px(1)} solid rgba(240,226,203,0.18)`, paddingTop: px(10) }}>
              {menu.notes.map((note, ni) => (
                <p
                  key={ni}
                  style={{
                    fontSize: px(10.5),
                    color: 'rgba(240,226,203,0.5)',
                    textAlign: 'center',
                    margin: 0,
                  }}
                >
                  {note}
                </p>
              ))}
            </div>
          )}
        </div>
      ) : (
        body && (
          <p style={{ fontSize: px(13), lineHeight: 1.6, color: 'rgba(240,226,203,0.72)', marginTop: px(14) }}>
            {body}
          </p>
        )
      )}

      <div style={{ marginTop: px(18) }}>
        <SocialBrewBadge rating={rating} ratingCount={ratingCount} tone="dark" scale={scale} />
      </div>
    </div>
  )
}
